import { useEffect, useMemo, useState } from "react";
import { PRODUCT_LISTING_PATH, PRODUCT_URL } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useAuthDetails } from "@/hooks/useAuthDetails";
import { formatToNaira } from "@/common/utils/general";
import { IProductListing } from "@/common/types/pricing";

export interface IIntakePricing {
  cost_price: string;
  selling_price: string;
  selling_level: number | null;
  /** The seller has never priced this product, so a price is mandatory here. */
  firstPricing: boolean;
  /** Entered price differs from the product's current one — a reprice. */
  wouldReprice: boolean;
  /** Every field this seller must supply is filled in. */
  complete: boolean;
}

interface IPricingDetailsProps {
  productId: string;
  value: IIntakePricing;
  onChange: (pricing: IIntakePricing) => void;
  disabled?: boolean;
}

// MED-686 — cost is only asked of companies whose stock does not arrive against
// a platform purchase order. For everyone else the backend captures the real
// figure from the order line at goods receipt, and anything typed here would be
// discarded, so the field is not shown at all.
const SELF_SOURCED_COMPANY_TYPES = ["Manufacturer", "Importer"];

export const EMPTY_INTAKE_PRICING: IIntakePricing = {
  cost_price: "",
  selling_price: "",
  selling_level: null,
  firstPricing: false,
  wouldReprice: false,
  complete: false,
};

// MED-686 — the Pricing details section, shared by the single upload's Step 1
// and the bulk upload's post-validation modal. One component so the two doors
// pre-populate, warn and validate identically; a user who learns one has learned
// the other only if there is one implementation.
//
// Selling price is a PRODUCT-level fact: what is typed here moves the price of
// every batch and every future sale, which is why editing a pre-populated value
// raises a warning rather than saving quietly. The server is the authority — it
// re-checks and returns 409 with both figures — so the banner here is a warning,
// not the gate.
export function PricingDetails({ productId, value, onChange, disabled = false }: IPricingDetailsProps) {
  const apiCall = useApiCallHandling();
  const { companyType } = useAuthDetails();

  const [listing, setListing] = useState<IProductListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Kept so the hint can say the cost was carried over rather than typed. A
  // stale cost applied silently to a new batch is a quiet data-quality problem:
  // the uploader has to be able to see it came from last time and correct it.
  const [priorCost, setPriorCost] = useState("");

  const asksForCost = SELF_SOURCED_COMPANY_TYPES.includes(companyType ?? "");

  useEffect(() => {
    if (!productId) {
      setListing(null);
      setPriorCost("");
      onChange(EMPTY_INTAKE_PRICING);
      return;
    }

    setLoading(true);
    setLoadError("");
    apiCall(
      `${PRODUCT_URL}/${productId}${PRODUCT_LISTING_PATH}`,
      "GET",
      {},
      (data: IProductListing) => {
        setListing(data);

        // The level the price is expressed at: the seller's chosen one, else the
        // outermost defined layer — the backend already resolves that fallback
        // into `selling_level`, so this never has to guess.
        const level = data?.selling_level ?? data?.levels?.[0]?.level ?? null;
        const entry = data?.levels?.find((l) => l.level === level);
        const current = data?.priced && entry?.price ? String(Number(entry.price)) : "";

        // Cost pre-populates from the last batch that recorded one, the same way
        // selling price pre-populates from the listing. `margin.cost_per_base_unit`
        // is MarginCalculator#latest_known_cost — the most recent positive
        // `cost_price_per_unit` this seller entered for this product — and it is
        // already PER BASE UNIT, which is exactly the unit this field submits
        // (`cost_price_per_unit`), so it carries over with no conversion.
        //
        // Unlike selling price, cost is a batch fact: re-submitting it unchanged
        // affects nothing beyond this batch, so there is no reprice warning to
        // raise. It is a convenience for the common case of stock arriving at the
        // same cost as last time, not a statement about the product.
        //
        // `cost_known` is the gate rather than a bare presence check: the margin
        // payload distinguishes "no cost on record" from a cost of zero, and a
        // zero would otherwise prefill as a real figure.
        const lastCost =
          data?.margin?.cost_known && data?.margin?.cost_per_base_unit
            ? String(Number(data.margin.cost_per_base_unit))
            : "";

        setPriorCost(lastCost);

        onChange({
          ...EMPTY_INTAKE_PRICING,
          cost_price: lastCost,
          selling_price: current,
          selling_level: level,
          firstPricing: !data?.priced,
          // Pre-populated and untouched is never a reprice.
          wouldReprice: false,
          complete: isComplete({ cost: lastCost, price: current, asksForCost }),
        });
      },
      () => setLoadError("Could not load this product's current price."),
      () => setLoading(false),
      { dontNotifyOnSucess: true, dontNotifyonFail: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const levelEntry = useMemo(
    () => listing?.levels?.find((l) => l.level === value.selling_level) ?? null,
    [listing, value.selling_level],
  );

  // The price this product is currently sold at, at the same level the field is
  // labelled with — so the warning compares like with like.
  const currentPriceAtLevel = useMemo(() => {
    if (!listing?.priced) return null;
    const entry = listing?.levels?.find((l) => l.level === value.selling_level);
    return entry?.price ? Number(entry.price) : null;
  }, [listing, value.selling_level]);

  function isComplete({ cost, price, asksForCost: asks }: { cost: string; price: string; asksForCost: boolean }) {
    const priceOk = Number(price) > 0;
    const costOk = !asks || Number(cost) > 0;
    return priceOk && costOk;
  }

  function update(patch: Partial<IIntakePricing>) {
    const next = { ...value, ...patch };
    const typed = Number(next.selling_price);

    onChange({
      ...next,
      // A reprice only when the product HAS a price and the entered figure
      // differs from it. Compared numerically: "2400" and "2400.00" are the same
      // price, and re-submitting an untouched pre-populated field must not warn.
      wouldReprice:
        currentPriceAtLevel !== null && !Number.isNaN(typed) && typed > 0 && typed !== currentPriceAtLevel,
      complete: isComplete({ cost: next.cost_price, price: next.selling_price, asksForCost }),
    });
  }

  const priceLabel = levelEntry?.packaging_type
    ? `Selling price per ${levelEntry.packaging_type} (NGN)`
    : "Selling price (NGN)";

  if (!productId) return null;

  return (
    <div className="mb-3 w-full rounded-lg border border-gray-200 p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-gray-800">Pricing details</span>
        {loading && <span className="text-xs text-gray-500">Loading current price…</span>}
        {!loading && listing?.priced && currentPriceAtLevel !== null && (
          <span className="text-xs text-gray-500">
            Current: {formatToNaira(currentPriceAtLevel)}
            {levelEntry?.packaging_type ? ` per ${levelEntry.packaging_type}` : ""}
          </span>
        )}
      </div>

      {loadError && <p className="mb-2 text-xs text-red-600">{loadError}</p>}

      <div className="flex flex-wrap gap-4">
        {asksForCost && (
          <label className="flex-1 min-w-[12rem] text-sm">
            <span className="mb-1 block text-gray-700">
              Cost price (NGN) <span className="text-red-500">*</span>
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full rounded border border-gray-300 p-2 text-sm"
              placeholder="What this batch cost you"
              value={value.cost_price}
              disabled={disabled || loading}
              onChange={(e) => update({ cost_price: e.target.value })}
            />
            {/* Cost is a batch fact: no warning, no product-wide effect. But a
                carried-over figure has to be visible as such — this batch may
                not have cost what the last one did, and a prefill nobody
                notices is how a wrong cost gets recorded without anyone
                choosing it. */}
            <span className="mt-1 block text-xs text-gray-500">
              {priorCost && value.cost_price === priorCost
                ? "Carried over from your last batch — change it if this one cost a different amount."
                : "Recorded against this batch only."}
            </span>
          </label>
        )}

        <label className="flex-1 min-w-[12rem] text-sm">
          <span className="mb-1 block text-gray-700">
            {priceLabel} <span className="text-red-500">*</span>
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="w-full rounded border border-gray-300 p-2 text-sm"
            placeholder={value.firstPricing ? "Set this product's price" : "Product price"}
            value={value.selling_price}
            disabled={disabled || loading}
            onChange={(e) => update({ selling_price: e.target.value })}
          />
          <span className="mt-1 block text-xs text-gray-500">
            {value.firstPricing
              ? "This becomes the product's price — required on a first upload."
              : "Applies to the whole product, not just this batch."}
          </span>
        </label>
      </div>

      {value.wouldReprice && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
          <span className="font-medium">This changes the price of the entire product.</span>{" "}
          Every batch and every future sale will use{" "}
          {Number(value.selling_price) > 0 ? formatToNaira(Number(value.selling_price)) : "the new price"}
          {levelEntry?.packaging_type ? ` per ${levelEntry.packaging_type}` : ""} instead of{" "}
          {currentPriceAtLevel !== null ? formatToNaira(currentPriceAtLevel) : "the current price"}. You
          will be asked to confirm before it is applied.
        </div>
      )}
    </div>
  );
}
